function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@domain-group/fe-co-allhomes-design-system';
import { CloseIconSmall, SearchIcon } from '@domain-group/fe-co-icon';
import Modal from '@domain-group/fe-co-modal';
import Suggestions from '../components/suggestions';
import SelectedItemList from '../components/selected-items';
import Messages from '../components/messages';
import RecentItems from '../components/recent-items';
import useAutocomplete from '../utils/use-autocomplete';
import { MIN_QUERY_LENGTH } from '../utils/constants';
import * as autocompletePropTypes from '../utils/prop-types';
import { Header, Footer } from '.';
import * as styles from './autocomplete-modal.style';
import SuggestionHeading from '../components/suggestion-heading';
import { jsx as ___EmotionJSX } from "@emotion/core";
var AutocompleteBase = _ref => {
  var setQueryText = _ref.setQueryText,
    queryText = _ref.queryText,
    suggestions = _ref.suggestions,
    initialSelectedItems = _ref.initialSelectedItems,
    types = _ref.types,
    immediateTypes = _ref.immediateTypes,
    buildGroupName = _ref.buildGroupName,
    placeholder = _ref.placeholder,
    hideButton = _ref.hideButton,
    singleSelect = _ref.singleSelect,
    showEmptySheet = _ref.showEmptySheet,
    _ref$title = _ref.title,
    title = _ref$title === void 0 ? 'Search' : _ref$title,
    loading = _ref.loading,
    notFoundText = _ref.notFoundText,
    error = _ref.error,
    extensions = _ref.extensions,
    closeModal = _ref.closeModal,
    recentSearchKey = _ref.recentSearchKey,
    saveRecentOnSearch = _ref.saveRecentOnSearch,
    onLoadedRecentSearches = _ref.onLoadedRecentSearches,
    onImmediateSearch = _ref.onImmediateSearch,
    onSearch = _ref.onSearch,
    onRecentSearch = _ref.onRecentSearch,
    onSelectionChange = _ref.onSelectionChange,
    resetSuggestions = _ref.resetSuggestions,
    allowDefaultSearch = _ref.allowDefaultSearch;
  var handleSelectionChange = selectedItems => {
    if (singleSelect && (selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) > 0) {
      closeModal();
    }
    onSelectionChange(selectedItems);
  };
  var searchHandler = handler => handler && function () {
    setTimeout(() => {
      closeModal();
    }, 0);
    return handler(...arguments);
  };
  var _useAutocomplete = useAutocomplete({
      queryText,
      setQueryText,
      suggestions,
      initialSelectedItems,
      types,
      singleSelect,
      immediateTypes,
      onSearch: searchHandler(onSearch),
      onImmediateSearch: searchHandler(onImmediateSearch),
      onRecentSearch: searchHandler(onRecentSearch),
      onSelectionChange: handleSelectionChange,
      recentSearchKey,
      saveRecentOnSearch,
      onLoadedRecentSearches
    }),
    isOpen = _useAutocomplete.isOpen,
    filteredSuggestions = _useAutocomplete.filteredSuggestions,
    hasData = _useAutocomplete.hasData,
    selectedItems = _useAutocomplete.selectedItems,
    recentItems = _useAutocomplete.recentItems,
    highlightedIndex = _useAutocomplete.highlightedIndex,
    removeSelectedItem = _useAutocomplete.removeSelectedItem,
    resetSelectedItems = _useAutocomplete.resetSelectedItems,
    openMenu = _useAutocomplete.openMenu,
    closeMenu = _useAutocomplete.closeMenu,
    setInputValue = _useAutocomplete.setInputValue,
    onSearchButtonClick = _useAutocomplete.onSearchButtonClick,
    getComboboxProps = _useAutocomplete.getComboboxProps,
    getSelectedItemProps = _useAutocomplete.getSelectedItemProps,
    getInputProps = _useAutocomplete.getInputProps,
    getMenuProps = _useAutocomplete.getMenuProps,
    getItemProps = _useAutocomplete.getItemProps;
  var inputRef = useRef();
  var initialRender = useRef(true);
  var focusInput = () => {
    var _inputRef$current;
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  };
  var openAndFocus = () => {
    if (!isOpen) {
      openMenu();
    }
    focusInput();
  };
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      openAndFocus();
    }
  });
  var handleSearchButtonClick = () => {
    if (allowDefaultSearch || selectedItems.length > 0) {
      onSearchButtonClick();
    }
  };
  var handleDoneButtonClick = () => {
    onSearchButtonClick();
  };
  var handleModalClose = () => {
    closeModal();
    resetSelectedItems();
    resetSuggestions();
  };
  var handleClearButtonClick = event => {
    event.stopPropagation();
    setQueryText('');
    setInputValue('');
    closeMenu();
  };
  var showSheet = isOpen && (hasData || showEmptySheet || queryText.length >= MIN_QUERY_LENGTH || loading || error || recentItems.length > 0);
  var showSearchButton = !hideButton && (!singleSelect || !isOpen && queryText.length === 0);
  var showCloseButton = singleSelect && (queryText.length > 0 || isOpen);
  return ___EmotionJSX(Modal, {
    appearance: "action-sheet",
    initialFocusRef: inputRef,
    innerStyle: {
      width: '100%',
      height: '100%'
    },
    hideXToClose: true
  }, ___EmotionJSX("div", {
    css: styles.modal
  }, ___EmotionJSX(Header, {
    heading: title,
    handleClose: handleModalClose
  }), ___EmotionJSX("div", {
    css: styles.body
  }, ___EmotionJSX("div", {
    css: styles.scrollContainer
  }, ___EmotionJSX("div", {
    css: styles.autocomplete
  }, (extensions === null || extensions === void 0 ? void 0 : extensions.suggestionSheet) && ___EmotionJSX("div", {
    css: styles.embeddedComponent
  }, extensions.suggestionSheet), ___EmotionJSX("div", {
    onClick: openAndFocus,
    css: styles.inputWrapper
  }, ___EmotionJSX("div", _extends({
    css: styles.itemList
  }, getComboboxProps()), !(singleSelect && isOpen) && ___EmotionJSX(SelectedItemList, {
    behaviour: "wrap",
    selectedItems: selectedItems,
    singleSelect: singleSelect,
    getSelectedItemProps: getSelectedItemProps,
    removeSelectedItem: removeSelectedItem,
    noFocus: true
  }), ___EmotionJSX("input", _extends({
    key: "input",
    placeholder: !singleSelect && selectedItems.length > 0 ? '' : placeholder,
    css: styles.comboxInput({
      isOpen: isOpen || selectedItems.length === 0
    })
  }, getInputProps({
    ref: inputRef
  }), {
    enterKeyHint: "search",
    onKeyDown: event => {
      if (event.key === 'Backspace' && inputRef.current.value.length === 0) {
        // backspace with no active word being typed removes last pill
        removeSelectedItem(selectedItems.at(-1));
      }
    },
    onKeyUp: event => {
      if (event.key === 'Enter') {
        // enter key performs search
        // doing it on key up stops the modal from briefly reopening
        handleSearchButtonClick();
      }
    }
  })), showCloseButton && ___EmotionJSX(Button, {
    css: styles.closeButton,
    size: "small",
    appearance: "ghostNeutral",
    onClick: handleClearButtonClick,
    icon: ___EmotionJSX(CloseIconSmall, null)
  })), showSearchButton && ___EmotionJSX(Button, {
    onClick: handleSearchButtonClick,
    css: styles.searchButton,
    size: "small",
    appearance: "accent",
    icon: ___EmotionJSX(SearchIcon, null)
  })), ___EmotionJSX("div", {
    css: styles.optionSheetContainer
  }, ___EmotionJSX("ul", _extends({
    css: styles.optionSheet({
      isOpen: showSheet
    })
  }, getMenuProps()), isOpen && ___EmotionJSX(Fragment, null, recentItems.length ? ___EmotionJSX("li", null, ___EmotionJSX(SuggestionHeading, null, "Recent Searches"), ___EmotionJSX(RecentItems, {
    highlightedIndex: highlightedIndex,
    getItemProps: getItemProps,
    recentItems: recentItems
  })) : ___EmotionJSX("li", null, extensions !== null && extensions !== void 0 && extensions.suggestionHeading ? ___EmotionJSX("div", {
    css: styles.suggestionExtension
  }, extensions.suggestionHeading) : ___EmotionJSX(SuggestionHeading, null, "Suggestions"), ___EmotionJSX(Suggestions, {
    singleSelect: singleSelect,
    queryText: queryText,
    getItemProps: getItemProps,
    indexOffset: recentItems.length,
    highlightedIndex: highlightedIndex,
    suggestions: filteredSuggestions,
    immediateTypes: immediateTypes,
    buildGroupName: buildGroupName,
    onItemClick: () => setTimeout(() => {
      var clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true
      });
      inputRef.current.dispatchEvent(clickEvent);
    }, 200)
  }))), isOpen && !hasData && ___EmotionJSX("div", _extends({
    css: styles.message
  }, getMenuProps()), ___EmotionJSX(Messages, {
    loading: loading,
    error: error,
    queryText: queryText,
    notFoundText: notFoundText
  }))))))), ___EmotionJSX(Footer, {
    onClick: handleDoneButtonClick
  })));
};
AutocompleteBase.propTypes = {
  suggestions: autocompletePropTypes.valueList,
  recentItems: autocompletePropTypes.valueList,
  initialSelectedItems: autocompletePropTypes.valueList,
  size: autocompletePropTypes.size,
  hideButton: PropTypes.bool,
  singleSelect: PropTypes.bool,
  showEmptySheet: PropTypes.bool,
  types: autocompletePropTypes.types,
  immediateTypes: autocompletePropTypes.types,
  buildGroupName: autocompletePropTypes.buildGroupName,
  placeholder: PropTypes.string,
  extensions: PropTypes.shape({
    suggestionSheet: PropTypes.node,
    suggestionHeading: PropTypes.node
  }),
  title: PropTypes.string,
  recentSearchKey: PropTypes.string,
  saveRecentOnSearch: PropTypes.bool,
  className: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  notFoundText: PropTypes.string,
  queryText: PropTypes.string,
  setQueryText: PropTypes.func,
  closeModal: PropTypes.func,
  onSearch: PropTypes.func,
  onImmediateSearch: PropTypes.func,
  onRecentSearch: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onLoadedRecentSearches: PropTypes.func,
  resetSuggestions: PropTypes.func,
  allowDefaultSearch: PropTypes.bool.isRequired
};
AutocompleteBase.defaultProps = {
  suggestions: [],
  recentItems: [],
  initialSelectedItems: [],
  size: 'normal',
  hideButton: false,
  singleSelect: false,
  showEmptySheet: false,
  placeholder: '',
  extensions: undefined,
  types: [],
  immediateTypes: [],
  buildGroupName: undefined,
  title: 'Search',
  className: undefined,
  recentSearchKey: undefined,
  saveRecentOnSearch: undefined,
  loading: false,
  notFoundText: undefined,
  error: false,
  queryText: '',
  setQueryText: () => {},
  closeModal: () => {},
  onSearch: () => {},
  onImmediateSearch: undefined,
  onRecentSearch: undefined,
  onSelectionChange: undefined,
  onLoadedRecentSearches: undefined,
  resetSuggestions: () => {}
};
export default AutocompleteBase;